/** Content for /events (Figma: WCD Events v4, editorial index). */

/** Past events on the WCD Luma calendar. */
const LUMA_PAST_EVENTS_URL = "https://luma.com/wcd?period=past";
const YOUTUBE_CHANNEL_URL =
  "https://www.youtube.com/@world-class-design";

export const nextEvent = {
  day: "12 Nov",
  meta: "2026 · Station F",
  title: "Designing AI products people trust",
  speakers: "With Thibaud Eberwein (Proton) and Pierrick Thébault (Shine)",
  cover: "/events/11-12-ai-products-trust-lumacover-1.webp",
  registerUrl: "https://luma.com/01vwvdjr",
};

export const hero = {
  photo: "/events/05-22-design-leaders-dinner-r1-1.webp",
  // Caption names the event only, never the guests, the date or a round number.
  caption: "Design Leaders Dinner",
};

type FormatMedia =
  | { kind: "photo"; src: string; alt: string; height: 365 | 432 }
  | { kind: "cover"; src: string; alt: string; width: number; height: number };

export type EventFormat = {
  id: string;
  name: string;
  /** Short line under the name in the sticky index. */
  indexLine: string;
  title: string;
  description: string;
  proof: string;
  link: { label: string; href: string };
  media: FormatMedia;
  /** When set, the panel shows a photo carousel instead of the single media. */
  gallery?: Array<{ src: string; caption: string }>;
};

export const formats: EventFormat[] = [
  {
    id: "podcast",
    name: "Podcast",
    indexLine: "Long talks with design leaders",
    title: "Design leaders, on the record.",
    description:
      "Long conversations with the people who run and teach design. Published on YouTube.",
    proof: "Guests from NYU, Source Paris and The CX Evolutionist",
    link: { label: "See all episodes", href: YOUTUBE_CHANNEL_URL },
    media: {
      kind: "photo",
      src: "/events/09-04-podcast-ep04-katja-forbes-cover-1.webp",
      alt: "Podcast episode with Katja Forbes: Fixed customer journeys are worthless",
      height: 365,
    },
  },
  {
    id: "dinners",
    name: "Dinners",
    indexLine: "Small tables, invitation only",
    title: "Design Leaders Dinners",
    description:
      "A small table of heads of design and product. Candid talk about talent. Invitation only.",
    proof: "Co-hosted with Bpifrance's VP Product Design and Design Club",
    link: { label: "See the dinners", href: LUMA_PAST_EVENTS_URL },
    gallery: [
      {
        src: "/events/09-29-design-leaders-dinner-r2-4.webp",
        caption: "Design Leaders Dinner",
      },
      {
        src: "/events/05-22-design-leaders-dinner-r1-1.webp",
        caption: "Design Leaders Dinner",
      },
      {
        src: "/events/09-29-design-leaders-dinner-r2-6.webp",
        caption: "Design Leaders Dinner",
      },
      {
        src: "/events/05-22-design-leaders-dinner-r1-3.webp",
        caption: "Design Leaders Dinner",
      },
      {
        src: "/events/09-29-design-leaders-dinner-r2-3.webp",
        caption: "Design Leaders Dinner",
      },
      {
        src: "/events/05-22-design-leaders-dinner-r1-5.webp",
        caption: "Design Leaders Dinner",
      },
    ],
    media: {
      kind: "photo",
      src: "/events/09-29-design-leaders-dinner-r2-4.webp",
      alt: "Guests laughing at the second Design Leaders Dinner",
      height: 432,
    },
  },
  {
    id: "breakfasts",
    name: "Founders Breakfasts",
    indexLine: "Founders ask, one guest answers",
    title: "Founders Breakfasts",
    description:
      "Early sessions where founders get straight answers on design and product from one invited guest.",
    proof: "Guests from IDEO, Upstream and e-Residency",
    link: { label: "See the breakfasts", href: LUMA_PAST_EVENTS_URL },
    gallery: [
      {
        src: "/events/09-23-founders-breakfast-03-3253.webp",
        caption: "Founders Breakfast",
      },
      {
        src: "/events/09-23-founders-breakfast-03-3285.webp",
        caption: "Founders Breakfast",
      },
      {
        src: "/events/09-23-founders-breakfast-03-3263.webp",
        caption: "Founders Breakfast",
      },
      {
        src: "/events/09-23-founders-breakfast-03-3281.webp",
        caption: "Founders Breakfast",
      },
      {
        src: "/events/09-23-founders-breakfast-03-3262.webp",
        caption: "Founders Breakfast",
      },
      {
        src: "/events/09-23-founders-breakfast-03-3260.webp",
        caption: "Founders Breakfast",
      },
      {
        src: "/events/09-23-founders-breakfast-03-3280.webp",
        caption: "Founders Breakfast",
      },
    ],
    media: {
      kind: "photo",
      src: "/events/09-23-founders-breakfast-03-3253.webp",
      alt: "Lisa Baird speaking with founders at a Founders Breakfast",
      height: 432,
    },
  },
  {
    id: "workshops",
    name: "Workshops",
    indexLine: "Hands-on, laptops open",
    title: "Workshops",
    description:
      "Hands-on sessions on the tools hiring teams now expect from designers.",
    proof: "Led by working designers, including a former Shopify designer",
    link: { label: "See the workshops", href: LUMA_PAST_EVENTS_URL },
    media: {
      kind: "cover",
      src: "/events/04-16-claude-code-workshop-lumacover-1.webp",
      alt: "Claude Code for Design(ers) workshop cover",
      width: 370,
      height: 301,
    },
  },
  {
    id: "meetups",
    name: "Meetups",
    indexLine: "Talks, then drinks, at Station F",
    title: "Evening meetups",
    description:
      "Talks and open conversation with design leaders, then drinks, at Station F.",
    proof: "Speakers from Mistral AI, Doctolib, Qonto and Back Market",
    link: { label: "See all meetups", href: LUMA_PAST_EVENTS_URL },
    gallery: [
      {
        src: "/events/03-19-state-of-design-europe-6.webp",
        caption: "The State of Design in Europe",
      },
      {
        src: "/events/03-19-state-of-design-europe-2.webp",
        caption: "The State of Design in Europe",
      },
      {
        src: "/events/03-19-state-of-design-europe-4.webp",
        caption: "The State of Design in Europe",
      },
      {
        src: "/events/06-04-business-impact-portfolio-8.webp",
        caption: "Showing Business Impact",
      },
      {
        src: "/events/06-04-business-impact-portfolio-3.webp",
        caption: "Showing Business Impact",
      },
      {
        src: "/events/01-29-portfolio-reviews-ai-5.webp",
        caption: "Portfolio Reviews in the Age of AI",
      },
      {
        src: "/events/01-29-portfolio-reviews-ai-3.webp",
        caption: "Portfolio Reviews in the Age of AI",
      },
    ],
    media: {
      kind: "photo",
      src: "/events/03-19-state-of-design-europe-6.webp",
      alt: "A full room at The State of Design in Europe meetup at Station F",
      height: 432,
    },
  },
];

/** Photo band: wide and narrow photos alternate. Captions are event and date only. */
export const bandPhotos: Array<{
  src: string;
  caption: string;
  wide: boolean;
}> = [
  {
    src: "/events/01-29-portfolio-reviews-ai-5.webp",
    caption: "Portfolio Reviews",
    wide: true,
  },
  {
    src: "/events/01-29-portfolio-reviews-ai-3.webp",
    caption: "Portfolio Reviews",
    wide: false,
  },
  {
    src: "/events/03-19-state-of-design-europe-6.webp",
    caption: "State of Design in Europe",
    wide: true,
  },
  {
    src: "/events/03-19-state-of-design-europe-4.webp",
    caption: "State of Design",
    wide: false,
  },
  {
    src: "/events/05-22-design-leaders-dinner-r1-3.webp",
    caption: "Design Leaders Dinner",
    wide: true,
  },
  {
    src: "/events/05-22-design-leaders-dinner-r1-5.webp",
    caption: "Dinner",
    wide: false,
  },
  {
    src: "/events/06-04-business-impact-portfolio-8.webp",
    caption: "Showing Business Impact",
    wide: true,
  },
  {
    src: "/events/06-04-business-impact-portfolio-3.webp",
    caption: "Business Impact",
    wide: false,
  },
  {
    src: "/events/09-29-design-leaders-dinner-r2-6.webp",
    caption: "Design Leaders Dinner",
    wide: true,
  },
  {
    src: "/events/09-29-design-leaders-dinner-r2-3.webp",
    caption: "Dinner",
    wide: false,
  },
];

/** Photo slugs live in /public/events/people; logos in /public/events/logos (wordmarks keep their width). */
type Logo = { src: string; width: number };
const logo = (file: string, width = 16): Logo => ({
  src: `/events/logos/${file}.webp`,
  width,
});

/** Ordered by how widely recognised each company is internationally, best known first. */
export const speakers: Array<{
  name: string;
  company: string;
  /** File slug in /public/events/people; omit until we have a photo (initials show instead). */
  photo?: string;
  /** Omit when the company has no confirmed logo; only the name shows then. */
  logo?: Logo;
}> = [
  // Opened "AI Design & Vibe Coding", hosted at Mistral AI's office, Jun 2025.
  {
    name: "Zach Krasner",
    company: "Mistral AI",
    photo: "zach-krasner",
    logo: logo("mistral-ai"),
  },
  {
    name: "Regine Gilbert",
    company: "NYU",
    photo: "regine-gilbert",
    logo: logo("nyu-edu"),
  },
  {
    name: "Julien Clement",
    company: "Carrefour",
    photo: "julien-clement",
    logo: logo("carrefour-com"),
  },
  {
    name: "Anne Asensio",
    company: "Dassault Systèmes",
    photo: "anne-asensio",
    logo: logo("3ds-com"),
  },
  {
    name: "Thibaud Eberwein",
    company: "Proton",
    photo: "thibaud-eberwein",
    logo: logo("proton-me"),
  },
  {
    name: "Raphaël Lorand",
    company: "Doctolib",
    photo: "raphael-lorand",
    logo: logo("doctolib-fr"),
  },
  {
    name: "Théau Mesnard",
    company: "Back Market",
    photo: "theau-mesnard",
    logo: logo("backmarket-com"),
  },
  {
    name: "Didier Hilhorst",
    company: "Qonto",
    photo: "didier-hilhorst",
    logo: logo("qonto-com"),
  },
  {
    name: "Julien Hillion",
    company: "Qonto",
    photo: "julien-hillion",
    logo: logo("qonto-com"),
  },
  {
    name: "Jean-Baptiste Kaloya",
    company: "Bpifrance",
    photo: "jean-baptiste-kaloya",
    logo: logo("bpifrance-fr", 56),
  },
  {
    name: "Kristiina Kalda",
    company: "e-Residency",
    photo: "kristiina-kalda",
    logo: logo("e-resident-gov-ee"),
  },
  {
    name: "Pierrick Thébault",
    company: "Shine",
    photo: "pierrick-thebault",
    logo: logo("shine-fr"),
  },
  {
    name: "Louis Lecat",
    company: "Upstream",
    photo: "louis-lecat",
    logo: logo("upstream-do"),
  },
  // Guest at "AI & The Rise of Designer-Founders", Nov 2025.
  {
    name: "Bastien Bigot",
    company: "Corsearch",
    photo: "bastien-bigot",
    logo: logo("corsearch-com"),
  },
  {
    name: "Lisa Baird",
    company: "Fraîche",
    photo: "lisa-baird",
    logo: logo("fraichedesignthinking-com"),
  },
  {
    name: "Maxime Frère",
    company: "Source Paris",
    photo: "maxime-frere",
    logo: logo("source-paris"),
  },
  {
    name: "Jordan Warmoes-Nielsen",
    company: "The Design Crew",
    photo: "jordan-warmoes-nielsen",
    logo: logo("thedesigncrew-co"),
  },
  {
    name: "Katja Forbes",
    company: "The CX Evolutionist",
    photo: "katja-forbes",
    logo: logo("katjaforbes-com"),
  },
  // Company per his LinkedIn headline.
  {
    name: "Antoine Valot",
    company: "Postcapitalist Design Club",
    photo: "antoine-valot",
  },
  // Featured speaker at the same Mistral AI event.
  {
    name: "François Savard",
    company: "END Agency",
    photo: "francois-savard",
    logo: logo("end-agency"),
  },
];
